package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.milta.core.localization.AiCreditStrings
import com.milta.core.localization.LocalStrings
import com.milta.core.localization.OptimizationQuotaStrings
import com.milta.feature.aicredit.domain.entity.AdminAccountAiCredit
import com.milta.feature.aicredit.domain.entity.AiCreditSettings
import com.milta.feature.aicredit.domain.usecase.GetAiCreditSettingsUseCase
import com.milta.feature.aicredit.domain.usecase.UpdateAiCreditSettingsUseCase
import com.milta.feature.aicredit.presentation.AdminAccountAiCreditIntent
import com.milta.feature.aicredit.presentation.AdminAccountAiCreditState
import com.milta.feature.aicredit.presentation.AdminAiCreditSettingsIntent
import com.milta.feature.aicredit.presentation.AdminAiCreditSettingsState
import com.milta.feature.aicredit.presentation.AdminAiCreditSettingsViewModel
import com.milta.feature.aicredit.presentation.AiCreditFormat
import com.milta.feature.aicredit.presentation.AiCreditSettingsField
import com.milta.feature.aicredit.presentation.AiCreditSettingsFieldError
import com.milta.feature.aicredit.presentation.actionLabel
import com.milta.feature.aicredit.presentation.eur
import com.milta.feature.aicredit.presentation.percent
import com.milta.feature.optimizationquota.domain.entity.AdminQuotaAccountKind
import com.milta.feature.optimizationquota.domain.entity.OptimizationSubscriptionKind
import com.milta.feature.optimizationquota.presentation.OptimizationQuotaFormat
import com.milta.feature.optimizationquota.presentation.fillPlaceholders
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.koin.compose.koinInject

/**
 * MIL-98 — A1, Admin "Limites d'usage", tab "Tokens IA" (shown "À venir" since MIL-97, now active):
 * the 4 limits in euros with their weighted-token equivalent computed client-side
 * (`€ × usdPerEur × 1 000 000`), the euro/dollar rate and the low-credit threshold (R10, R12).
 */
@Composable
internal fun AdminAiCreditLimitsPanel() {
    val strings = LocalStrings.current
    val ai = strings.aiCredit
    val quotaStrings = strings.optimizationQuota
    val getSettings: GetAiCreditSettingsUseCase = koinInject()
    val updateSettings: UpdateAiCreditSettingsUseCase = koinInject()
    val viewModel = remember { AdminAiCreditSettingsViewModel(getSettings, updateSettings, ai.decimalSeparator) }
    DisposableEffect(viewModel) { onDispose { viewModel.clear() } }
    val state by viewModel.state.collectAsState()
    LaunchedEffect(viewModel) { viewModel.onIntent(AdminAiCreditSettingsIntent.Load) }

    when {
        state.isLoading -> WebSpinner(size = 22, strokeWidth = 2)
        state.loadError != null -> SheetLoadError(
            message = if (state.loadError == "offline") quotaStrings.errorNoConnection else quotaStrings.errorGeneric,
            onRetry = { viewModel.onIntent(AdminAiCreditSettingsIntent.Load) },
            retryLabel = quotaStrings.retryAction,
        )
        else -> Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "18px") } }) {
            AdminPanelCard {
                Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "8px"); property("margin-bottom", "8px") } }) {
                    Div(attrs = { style { property("font-size", "15px"); property("font-weight", "700"); property("color", WebTextPrimary) } }) { Text(ai.limitsEyebrow) }
                }
                Div(attrs = { style { property("font-size", "13px"); property("color", WebTextSecondary); property("line-height", "1.5"); property("margin-bottom", "16px") } }) {
                    Text(ai.limitsDescription)
                }
                Div(attrs = { style { property("display", "grid"); property("grid-template-columns", "1.4fr 1fr 1fr"); property("gap", "10px 12px"); property("align-items", "start") } }) {
                    Div {}
                    AiColumnLabel(quotaStrings.columnTrial)
                    AiColumnLabel(quotaStrings.columnPaid)
                    AiRowLabel(quotaStrings.rowEnterprise)
                    EurLimitInput(state, ai, quotaStrings, AiCreditSettingsField.EnterpriseTrial, viewModel::onIntent)
                    EurLimitInput(state, ai, quotaStrings, AiCreditSettingsField.EnterprisePaid, viewModel::onIntent)
                    AiRowLabel(quotaStrings.rowDriver)
                    EurLimitInput(state, ai, quotaStrings, AiCreditSettingsField.DriverTrial, viewModel::onIntent)
                    EurLimitInput(state, ai, quotaStrings, AiCreditSettingsField.DriverPaid, viewModel::onIntent)
                }
                Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary); property("margin-top", "14px"); property("line-height", "1.5") } }) {
                    Text(ai.limitsFootnote)
                }
            }
            AdminPanelCard {
                Div(attrs = {
                    style {
                        property("font-size", "11px"); property("font-weight", "700"); property("letter-spacing", "0.06em")
                        property("text-transform", "uppercase"); property("color", WebTextSecondary); property("margin-bottom", "12px")
                    }
                }) { Text(ai.conversionEyebrow) }
                Div(attrs = { style { property("display", "flex"); property("gap", "28px"); property("flex-wrap", "wrap") } }) {
                    Div {
                        AiFieldLabel(ai.rateLabel)
                        Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "6px") } }) {
                            AiUnit(ai.rateUnitPrefix)
                            AiSettingsInput(state, AiCreditSettingsField.UsdPerEur, widthPx = 90, onIntent = viewModel::onIntent)
                            AiUnit(ai.rateUnitSuffix)
                        }
                        AiFieldMessage(state, ai, quotaStrings, AiCreditSettingsField.UsdPerEur, hint = ai.rateHint)
                    }
                    Div {
                        AiFieldLabel(ai.thresholdLabel)
                        Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "6px") } }) {
                            AiSettingsInput(state, AiCreditSettingsField.LowThreshold, widthPx = 70, onIntent = viewModel::onIntent)
                            AiUnit(ai.thresholdUnit)
                        }
                        AiFieldMessage(state, ai, quotaStrings, AiCreditSettingsField.LowThreshold, hint = ai.thresholdHint)
                    }
                }
                Div(attrs = {
                    style {
                        property("margin-top", "14px"); padding(12.px, 14.px); property("border-radius", "10px")
                        property("background", WebSurfaceDim); property("font-size", "12.5px"); property("line-height", "1.5"); property("color", WebTextPrimary)
                    }
                }) {
                    B { Text(ai.calculationTitle) }
                    Text(" ${ai.calculationBody}")
                }
            }
            val saved = state.savedSettings
            if (saved != null) InlineBanner(kind = InlineBannerKind.Success, text = savedText(saved, ai))
            val saveError = state.saveError
            if (saveError != null) {
                Div(attrs = { style { property("font-size", "12.5px"); property("color", WebErrorInk) } }) {
                    Text(
                        when (saveError) {
                            "validation" -> quotaStrings.limitsRejected
                            "offline" -> quotaStrings.errorNoConnection
                            else -> quotaStrings.errorGeneric
                        },
                    )
                }
            }
            Div {
                Button(attrs = {
                    if (!state.isSaving) onClick { viewModel.onIntent(AdminAiCreditSettingsIntent.Save) }
                    style {
                        gradientButtonStyle(this)
                        if (state.isSaving) { property("opacity", "0.5"); property("cursor", "not-allowed"); property("box-shadow", "none") }
                    }
                }) {
                    if (state.isSaving) WebSpinner(size = 16, strokeWidth = 2, color = "#FFFFFF", trackColor = "rgba(255,255,255,0.35)")
                    else Text(quotaStrings.saveAction)
                }
            }
        }
    }
}

private fun savedText(saved: AiCreditSettings, ai: AiCreditStrings): String =
    ai.settingsSavedTitle + " " + ai.settingsSavedDetail.fillPlaceholders(
        "enterpriseTrial" to ai.eur(saved.enterpriseTrialEur),
        "enterprisePaid" to ai.eur(saved.enterprisePaidEur),
        "driverTrial" to ai.eur(saved.driverTrialEur),
        "driverPaid" to ai.eur(saved.driverPaidEur),
        "rate" to AiCreditFormat.decimal(saved.usdPerEur, 4, ai.decimalSeparator).trimEnd('0').removeSuffix(ai.decimalSeparator),
        "threshold" to saved.lowThresholdPercent,
    )

@Composable
private fun AdminPanelCard(content: @Composable () -> Unit) {
    Div(attrs = {
        style { property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "12px"); padding(18.px) }
    }) { content() }
}

@Composable
private fun AiColumnLabel(label: String) {
    Div(attrs = {
        style {
            property("font-size", "11px"); property("font-weight", "700"); property("color", WebTextSecondary)
            property("text-transform", "uppercase"); property("letter-spacing", "0.05em")
        }
    }) { Text(label) }
}

@Composable
private fun AiRowLabel(label: String) {
    Div(attrs = { style { property("font-size", "13.5px"); property("font-weight", "600"); property("color", WebTextPrimary); property("padding-top", "9px") } }) {
        Text(label)
    }
}

@Composable
private fun AiFieldLabel(label: String) {
    Div(attrs = { style { property("font-size", "12.5px"); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "6px") } }) {
        Text(label)
    }
}

@Composable
private fun AiUnit(text: String) {
    Span(attrs = { style { property("font-size", "13px"); property("color", WebTextSecondary); property("white-space", "nowrap") } }) { Text(text) }
}

@Composable
private fun AiSettingsInput(
    state: AdminAiCreditSettingsState,
    field: AiCreditSettingsField,
    widthPx: Int?,
    onIntent: (AdminAiCreditSettingsIntent) -> Unit,
) {
    val error = state.fieldErrors[field]
    Input(
        type = InputType.Text,
        attrs = {
            attr("inputmode", if (field == AiCreditSettingsField.LowThreshold) "numeric" else "decimal")
            value(state.values[field].orEmpty())
            onInput { event -> onIntent(AdminAiCreditSettingsIntent.UpdateField(field, event.value)) }
            style {
                if (widthPx != null) property("width", "${widthPx}px") else property("width", "100%")
                property("box-sizing", "border-box"); padding(8.px, 10.px)
                property("border-radius", "8px"); property("font-size", "14px"); property("color", WebTextPrimary)
                property("border", "1.5px solid " + if (error != null) WebError else WebBorder)
            }
        },
    )
}

/** A limit in euros, with "≈ 3 261 000 tokens pondérés" under it — recomputed with the rate typed. */
@Composable
private fun EurLimitInput(
    state: AdminAiCreditSettingsState,
    ai: AiCreditStrings,
    quotaStrings: OptimizationQuotaStrings,
    field: AiCreditSettingsField,
    onIntent: (AdminAiCreditSettingsIntent) -> Unit,
) {
    Div {
        Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "6px") } }) {
            AiSettingsInput(state, field, widthPx = null, onIntent = onIntent)
            AiUnit("€")
        }
        val amount = state.values[field]?.let(AiCreditFormat::parseDecimal)
        val rate = state.usdPerEur
        val hint = if (amount != null && amount > 0 && rate != null) {
            ai.weightedEquivalent.fillPlaceholders("tokens" to AiCreditFormat.grouped(AiCreditFormat.weightedTokensForEur(amount, rate)))
        } else {
            null
        }
        AiFieldMessage(state, ai, quotaStrings, field, hint = hint)
    }
}

@Composable
private fun AiFieldMessage(
    state: AdminAiCreditSettingsState,
    ai: AiCreditStrings,
    quotaStrings: OptimizationQuotaStrings,
    field: AiCreditSettingsField,
    hint: String?,
) {
    val error = state.fieldErrors[field]
    val text = when (error) {
        AiCreditSettingsFieldError.Required -> quotaStrings.fieldRequired
        AiCreditSettingsFieldError.MustBePositive -> quotaStrings.fieldMustBePositive
        AiCreditSettingsFieldError.LimitTooHigh -> ai.limitTooHigh
        AiCreditSettingsFieldError.RateTooHigh -> ai.rateTooHigh
        AiCreditSettingsFieldError.ThresholdOutOfRange -> ai.thresholdOutOfRange
        null -> hint
    } ?: return
    Div(attrs = {
        style { property("font-size", "11.5px"); property("margin-top", "6px"); property("color", if (error != null) WebErrorInk else WebTextSecondary) }
    }) { Text(text) }
}

// ── A2 — Fiche utilisateur, bloc "Crédit IA" ──────────────────────────────────

/**
 * MIL-98 — A2, block "Crédit IA" of the Admin "Fiche utilisateur", under "Optimisations RoutaR":
 * remaining credit in euros and percent, applied and global limits, reset date, "Remplacer le crédit
 * restant" (0 to 100 €, current period only, R11).
 */
@Composable
internal fun AdminAiCreditCard(
    account: AdminAccountAiCredit,
    state: AdminAccountAiCreditState,
    onIntent: (AdminAccountAiCreditIntent) -> Unit,
) {
    val ai = LocalStrings.current.aiCredit
    val quotaStrings = LocalStrings.current.optimizationQuota
    val credit = account.credit
    AdminSheetCard(title = ai.adminCreditTitle) {
        val replacement = state.lastReplacement
        if (replacement != null) {
            InlineBanner(
                kind = InlineBannerKind.Success,
                text = ai.replacedTitle.fillPlaceholders("from" to ai.eur(replacement.fromEur), "to" to ai.eur(replacement.toEur)) + " " +
                    ai.replacedDetail.fillPlaceholders("date" to OptimizationQuotaFormat.fullDate(credit.resetAt), "limit" to ai.eur(credit.globalLimitEur)),
            )
        }
        Div(attrs = { style { property("display", "flex"); property("align-items", "baseline"); property("gap", "8px"); property("flex-wrap", "wrap") } }) {
            Div(attrs = {
                style {
                    property("font-size", "26px"); property("font-weight", "800")
                    property("color", when { credit.isExhausted -> WebErrorInk; credit.isLow -> WebLowInk; else -> WebPrimary })
                }
            }) { Text(ai.eur(credit.remainingEur)) }
            Div(attrs = { style { property("font-size", "13px"); property("color", WebTextSecondary) } }) {
                Text("/ ${ai.eur(credit.limitEur)} · ${ai.percent(credit.remainingPercent)}")
            }
        }
        CreditMeter(credit.remainingPercent, low = credit.isLow)
        Div(attrs = { style { property("display", "flex"); property("gap", "6px"); property("flex-wrap", "wrap"); property("margin", "4px 0") } }) {
            when {
                credit.isExhausted -> AdminStatusChip(ai.adminExhaustedPill, WebErrorInk, WebErrorBg)
                credit.isLow -> AdminStatusChip(
                    state.lowThresholdPercent?.let { ai.adminLowPill.fillPlaceholders("threshold" to it) } ?: ai.adminLowPillNoThreshold,
                    WebLowInk,
                    WebLowBg,
                )
            }
            if (credit.isManuallyAdjusted) AdminStatusChip(quotaStrings.manuallyAdjustedBadge, WebPrimary, WebActiveTint)
        }
        Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary); property("margin-bottom", "4px") } }) {
            Text(
                if (credit.remainingEur < 0) {
                    ai.adminOverrunLine.fillPlaceholders("consumed" to ai.eur(credit.consumedEur), "over" to ai.eur(-credit.remainingEur))
                } else {
                    ai.adminRemainingLine.fillPlaceholders(
                        "tokens" to AiCreditFormat.grouped(credit.remainingWeightedTokens),
                        "consumed" to ai.eur(credit.consumedEur),
                    )
                },
            )
        }
        val limitSource = limitSourceLabel(account, quotaStrings)
        if (credit.isManuallyAdjusted) {
            AdminSheetRow(
                quotaStrings.appliedLimitLabel,
                ai.eur(credit.limitEur),
                ai.adminAppliedAfterReplacement.fillPlaceholders(
                    "consumed" to ai.eur(credit.consumedEur),
                    "remaining" to ai.eur(credit.remainingEur),
                ),
            )
            AdminSheetRow(ai.adminGlobalLimitLabel, ai.eur(credit.globalLimitEur), ai.adminGlobalLimitResumes.fillPlaceholders("source" to limitSource))
        } else {
            AdminSheetRow(quotaStrings.appliedLimitLabel, ai.eur(credit.limitEur), limitSource)
        }
        val resetSource = when {
            credit.isExhausted -> ai.adminResetNoCarryOver.fillPlaceholders("limit" to ai.eur(credit.globalLimitEur))
            account.accountKind == AdminQuotaAccountKind.EnterpriseDriver && account.enterpriseName != null ->
                quotaStrings.resetSourceEnterprise.fillPlaceholders("enterprise" to account.enterpriseName.orEmpty())
            else -> quotaStrings.resetSourceOwn
        }
        AdminSheetRow(quotaStrings.resetLabel, OptimizationQuotaFormat.fullDate(credit.resetAt), resetSource, last = true)

        Div(attrs = { style { property("margin-top", "14px"); property("padding-top", "14px"); property("border-top", "1px solid $WebSurfaceDim") } }) {
            Div(attrs = { style { property("font-size", "12.5px"); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "6px") } }) {
                Text(ai.replaceLabel)
            }
            Div(attrs = { style { property("display", "flex"); property("gap", "8px"); property("align-items", "center") } }) {
                Input(
                    type = InputType.Text,
                    attrs = {
                        attr("inputmode", "decimal")
                        attr("placeholder", AiCreditFormat.eurInput(credit.remainingEur.coerceAtLeast(0.0), ai.decimalSeparator))
                        value(state.remainingInput)
                        onInput { event -> onIntent(AdminAccountAiCreditIntent.UpdateRemainingInput(event.value)) }
                        style {
                            property("width", "110px"); padding(8.px, 10.px); property("border-radius", "8px"); property("font-size", "14px")
                            property("border", "1.5px solid " + if (state.remainingInputInvalid || state.replaceError != null) WebError else WebBorder)
                        }
                    },
                )
                AiUnit("€")
                Button(attrs = {
                    if (!state.isReplacing) onClick { onIntent(AdminAccountAiCreditIntent.ReplaceRemaining) }
                    style {
                        secondaryButtonStyle(this)
                        if (state.isReplacing) { property("opacity", "0.5"); property("cursor", "not-allowed") }
                    }
                }) {
                    if (state.isReplacing) WebSpinner(size = 14, strokeWidth = 2) else Text(quotaStrings.replaceAction)
                }
            }
            val replaceError = when {
                state.remainingInputInvalid -> ai.replaceInvalid
                state.replaceError == "validation" -> ai.replaceInvalid
                state.replaceError == "offline" -> quotaStrings.errorNoConnection
                state.replaceError != null -> quotaStrings.errorGeneric
                else -> null
            }
            if (replaceError != null) {
                Div(attrs = { style { property("font-size", "11.5px"); property("color", WebErrorInk); property("margin-top", "4px") } }) { Text(replaceError) }
            }
            Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary); property("margin-top", "8px"); property("line-height", "1.45") } }) {
                Text(
                    if (account.accountKind == AdminQuotaAccountKind.EnterpriseDriver) {
                        ai.replaceHelpEnterpriseDriver.fillPlaceholders("name" to "${account.account.firstName} ${account.account.lastName}".trim())
                    } else {
                        ai.replaceHelp
                    },
                )
            }
        }
    }
}

/** "Limite Compte Entreprise · payant" (+ "(l'Entreprise est en essai)") — same wording as the MIL-97 block. */
private fun limitSourceLabel(account: AdminAccountAiCredit, strings: OptimizationQuotaStrings): String = buildString {
    val kind = if (account.accountKind == AdminQuotaAccountKind.EnterpriseOwner) strings.limitKindEnterprise else strings.limitKindDriver
    val subscription = when (account.credit.subscriptionKind) {
        OptimizationSubscriptionKind.Trial -> strings.subscriptionTrial
        OptimizationSubscriptionKind.Paid -> strings.subscriptionPaid
    }
    append("$kind · ${subscription.lowercase()}")
    if (account.accountKind == AdminQuotaAccountKind.EnterpriseDriver && account.credit.subscriptionKind == OptimizationSubscriptionKind.Trial) {
        append(" ${strings.enterpriseInTrialSuffix}")
    }
}

/** MIL-98 — A2, "Historique du crédit IA": period summary and the calls with tokens and cost (R3, R13). */
@Composable
internal fun AdminAiCreditHistoryCard(
    account: AdminAccountAiCredit?,
    state: AdminAccountAiCreditState,
    onIntent: (AdminAccountAiCreditIntent) -> Unit,
) {
    val ai = LocalStrings.current.aiCredit
    val quotaStrings = LocalStrings.current.optimizationQuota
    AdminSheetCard(title = ai.adminHistoryTitle) {
        val credit = account?.credit
        if (credit != null) {
            Div(attrs = { style { property("font-size", "12.5px"); property("color", WebTextSecondary); property("margin-bottom", "10px"); property("line-height", "1.45") } }) {
                Text(
                    ai.adminPeriodSummary.fillPlaceholders(
                        "start" to OptimizationQuotaFormat.fullDate(credit.periodStart),
                        "calls" to credit.periodCallCount,
                        "failed" to credit.periodFailedOrUnreadableCount,
                        "tokens" to AiCreditFormat.grouped(credit.periodWeightedTokens),
                        "consumed" to ai.eur(credit.consumedEur),
                    ),
                )
            }
        }
        AiCreditHistoryTable(
            history = state.history,
            columns = listOf(
                AiHistoryColumn(quotaStrings.columnDate),
                AiHistoryColumn(quotaStrings.columnAction),
                AiHistoryColumn(quotaStrings.columnOutcome),
                AiHistoryColumn(ai.columnInput, alignRight = true),
                AiHistoryColumn(ai.columnOutput, alignRight = true),
                AiHistoryColumn(ai.columnWeighted, alignRight = true),
                AiHistoryColumn(ai.columnCost, alignRight = true),
            ),
            footerStrings = quotaStrings,
            onLoadMore = { onIntent(AdminAccountAiCreditIntent.LoadMoreHistory) },
            maxHeightPx = 420,
        ) { entry ->
            AiHistoryCell(OptimizationQuotaFormat.dayMonthTime(entry.occurredAt))
            AiHistoryCell(ai.actionLabel(entry.action))
            AiHistoryCellContent { AiOutcomePill(entry.outcome, ai) }
            AiHistoryCell(AiCreditFormat.grouped(entry.inputTokens), alignRight = true)
            AiHistoryCell(AiCreditFormat.grouped(entry.outputTokens), alignRight = true)
            AiHistoryCell(AiCreditFormat.grouped(entry.weightedTokens), alignRight = true)
            AiHistoryCell(ai.eur(entry.costEur, decimals = 3), alignRight = true, bold = true)
        }
    }
}
